import { styled, SxProps, Theme, Tooltip } from '@mui/material';
import * as React from 'react';
import { type TagFilterKeys } from '@shared/filters';
import { datasets } from '~/scenes/datasets';

interface TagChipProps {
  tag: TagFilterKeys;
  sx?: SxProps<Theme>;
}

type ChipColor =
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning'
  | 'default';

const colorMap: Record<ChipColor, string> = {
  primary: '#1976d2',
  secondary: '#9c27b0',
  error: '#d32f2f',
  info: '#0288d1',
  success: '#2e7d32',
  warning: '#ed6c02',
  default: '#9e9e9e',
};

function buttonMaxWidth({ hovered }: { hovered: boolean }) {
  return hovered ? '300px' : '24px';
}
function buttonPadding({ hovered }: { hovered: boolean }) {
  return hovered ? '0 8px' : '0';
}
function buttonFontSize({ hovered }: { hovered: boolean }) {
  return hovered ? '0.75rem' : '0.6rem';
}
function buttonBgColor({ bgColor }: { bgColor: string }) {
  return bgColor;
}

const ChipWrapper = styled('span')<{ hovered: boolean }>`
  display: inline-flex;
  align-items: center;
  width: 24px;
  height: 24px;
  position: relative;
  flex-shrink: 0;
  z-index: ${({ hovered }) => (hovered ? 2 : 'auto')};
`;

const StyledButton = styled('button')<{ bgColor: string; hovered: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;
  background-color: ${buttonBgColor};
  color: #fff;
  border-radius: 999px;
  cursor: pointer;
  overflow: hidden;
  height: 24px;
  min-width: 24px;
  width: max-content;
  max-width: ${buttonMaxWidth};
  padding: ${buttonPadding};
  font-size: ${buttonFontSize};
  font-weight: bold;
  font-family: inherit;
  white-space: nowrap;
  user-select: none;
  border: none;
  outline: none;
  transition:
    max-width 0.25s ease,
    padding 0.25s ease,
    font-size 0.15s ease;
`;

function initialsOpacity({ hovered }: { hovered: boolean }) {
  return hovered ? 0 : 1;
}
function initialsTransition({ hovered }: { hovered: boolean }) {
  return hovered ? 'opacity 0.1s ease' : 'opacity 0.05s ease';
}

const Initials = styled('span')<{ hovered: boolean }>`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;
  opacity: ${initialsOpacity};
  transition: ${initialsTransition};
`;

function labelOpacity({ hovered }: { hovered: boolean }) {
  return hovered ? 1 : 0;
}
function labelTransition({ hovered }: { hovered: boolean }) {
  return hovered ? 'opacity 0.15s ease 0.1s' : 'opacity 0.05s ease';
}

const Label = styled('span')<{ hovered: boolean }>`
  pointer-events: none;
  opacity: ${labelOpacity};
  transition: ${labelTransition};
`;

export const TagChip = React.memo(({ tag, sx }: TagChipProps) => {
  const filter = datasets[tag];
  const label = filter?.chip || tag;
  const color = (filter?.color || 'primary') as ChipColor;
  const description = filter?.name || tag;
  const bgColor = colorMap[color] ?? colorMap.primary;

  const [hovered, setHovered] = React.useState(false);
  const [tooltipOpen, setTooltipOpen] = React.useState(false);

  const initials = label
    .split(' ')
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <Tooltip
      title={description}
      open={tooltipOpen}
      onClose={() => setTooltipOpen(false)}
      disableFocusListener
      disableHoverListener
      disableTouchListener>
      <ChipWrapper sx={sx} hovered={hovered}>
        <StyledButton
          type="button"
          bgColor={bgColor}
          hovered={hovered}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={() => setTooltipOpen((open) => !open)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setTooltipOpen((open) => !open);
            }
          }}
          aria-label={description}>
          <Initials hovered={hovered}>{initials}</Initials>
          <Label hovered={hovered}>{label}</Label>
        </StyledButton>
      </ChipWrapper>
    </Tooltip>
  );
});

TagChip.displayName = 'TagChip';
